/* Draft integration: Astra exposes an open shadow root, but no panel-size setting.
   Keep overrides isolated here so a vendor update can be handled or reverted. */
(() => {
  let attempts = 0;
  function attach() {
    const host = document.getElementById('astra-widget-container');
    const root = host?.shadowRoot;
    if (!root) {
      if (++attempts < 60) window.setTimeout(attach, 500);
      return;
    }
    if (root.getElementById('sn-astra-panel-style')) return;
    const style = document.createElement('style');
    style.id = 'sn-astra-panel-style';
    style.textContent = `
      :host([data-sn-astra-aligned]) #astra-widget-button {
        bottom: var(--sn-astra-launcher-bottom) !important;
        left: auto !important;
        right: var(--sn-astra-launcher-right) !important;
        width: var(--sn-astra-launcher-size) !important;
        height: var(--sn-astra-launcher-size) !important;
        border-radius: 50% !important;
        background: var(--astra-primary) !important;
        box-shadow: 0 3px 12px #0002;
      }
      :host([data-sn-astra-aligned]) #astra-widget-button > svg > g {
        filter: none !important;
      }
      :host([data-sn-astra-aligned]) #astra-widget-button > svg > g > circle:not(.red-dot) {
        display: none;
      }
      :host([data-sn-astra-aligned]) #astra-widget-minimized-label {
        bottom: calc(var(--sn-astra-launcher-bottom) + var(--sn-astra-launcher-size) * .61 + 5px) !important;
        left: auto !important;
        right: calc(var(--sn-astra-launcher-right) + var(--sn-astra-launcher-size) * .73 + 6px) !important;
        max-width: calc(100vw - var(--sn-astra-launcher-right) - var(--sn-astra-launcher-size) - 36px) !important;
        transform: none !important;
        overflow: visible !important;
      }
      #astra-widget-minimized-label .minimized-label-group {
        display: block !important;
        box-sizing: border-box;
        background: #fff !important;
        color: var(--astra-primary) !important;
        border: 0 !important;
        border-radius: 8px 8px 0 8px !important;
        padding: 5px 8px !important;
        font-family: var(--sn-astra-callout-font, 'Barlow Semi Condensed', sans-serif) !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: 1.12 !important;
        text-align: center;
        text-wrap: balance;
        overflow-wrap: anywhere;
        box-shadow: 0 2px 7px #18332912 !important;
        transform-origin: 100% 100%;
        scale: .8;
        transition: none !important;
        animation: sn-astra-caption 550ms 470ms both;
      }
      #astra-widget-minimized-label .minimized-label-close { display: none !important; }
      @keyframes sn-astra-caption {
        0% { transform: translate(-9px,4px) scale(.25) rotate(-18deg); opacity: 0; animation-timing-function: ease-out; }
        42% { transform: scale(1.1) rotate(5deg); opacity: 1; animation-timing-function: ease-in-out; }
        72% { transform: scale(.985) rotate(-1deg); opacity: 1; }
        100% { transform: scale(1) rotate(0); opacity: 1; }
      }
      @media (max-width: 600px) {
        #astra-widget-minimized-label .minimized-label-group {
          font-size: 13px !important;
          padding: 4px 7px !important;
          border-radius: 7px 7px 0 7px !important;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        #astra-widget-minimized-label .minimized-label-group { animation: none !important; }
      }
      #astra-widget { overflow: hidden; border-radius: 16px !important; }
      #astra-widget-iframe[style*="visibility: visible"] {
        visibility: var(--sn-astra-visibility, visible) !important;
      }
      #astra-widget:has(#astra-widget-iframe[style*="visibility: visible"]) {
        width: min(400px, calc(100vw - 30px)) !important;
        height: min(600px, calc(100dvh - 116px)) !important;
      }
      @media (min-width: 768px) {
        :host([data-sn-astra-aligned]) #astra-widget:has(#astra-widget-iframe[style*="visibility: visible"]) {
          left: auto !important;
          right: var(--sn-astra-launcher-right) !important;
          bottom: calc(var(--sn-astra-launcher-bottom) + var(--sn-astra-launcher-size) + 12px) !important;
          height: min(600px, calc(100dvh - var(--sn-astra-launcher-bottom) - var(--sn-astra-launcher-size) - 28px)) !important;
        }
      }
      @media (max-width: 767px) {
        #astra-widget:has(#astra-widget-iframe[style*="visibility: visible"]) {
          width: calc(100vw - 24px) !important;
          height: var(--sn-astra-panel-height, 70dvh) !important;
          left: 12px !important;
          right: auto !important;
          bottom: var(--sn-astra-panel-bottom, 12px) !important;
          margin-left: 0 !important;
          transition: opacity .2s ease !important;
        }
      }
    `;
    root.appendChild(style);
    host.style.setProperty('--sn-astra-callout-font', getComputedStyle(document.body).getPropertyValue('--BODY-FONT-FAMILY').trim() || getComputedStyle(document.body).fontFamily);
    function fitCallout() {
      const label = root.querySelector('#astra-widget-minimized-label .minimized-label-group');
      if (!label) return;
      const available = Math.max(80, document.documentElement.clientWidth -
        parseFloat(host.style.getPropertyValue('--sn-astra-launcher-right') || '24') -
        parseFloat(host.style.getPropertyValue('--sn-astra-launcher-size') || '62') * .73 - 24);
      label.style.setProperty('max-width', `${available}px`, 'important');
      label.style.setProperty('width', 'max-content', 'important');
      const appearance = getComputedStyle(label);
      const maxHeight = parseFloat(appearance.lineHeight) * 2 + parseFloat(appearance.paddingTop) + parseFloat(appearance.paddingBottom) + 1;
      let lower = Math.min(60, available), upper = Math.min(label.offsetWidth, available);
      while (upper - lower > 1) {
        const width = (lower + upper) / 2;
        label.style.setProperty('width', `${width}px`, 'important');
        if (label.offsetHeight <= maxHeight) upper = width; else lower = width;
      }
      label.style.setProperty('width', `${Math.ceil(upper)}px`, 'important');
    }
    // React recreates the label after closing the chat; fit it again then.
    new MutationObserver(fitCallout).observe(root, { childList: true, characterData: true, subtree: true });
    document.fonts?.ready.then(fitCallout);
    const whatsapp = document.querySelector('.sn-whatsapp');
    function alignLaunchers() {
      if (!whatsapp) return;
      const appearance = getComputedStyle(whatsapp);
      const bottom = parseFloat(appearance.bottom);
      const height = whatsapp.getBoundingClientRect().height;
      if (!Number.isFinite(bottom) || !height) return;
      // Stack above WhatsApp, with matching diameter and a 12px gap.
      host.style.setProperty('--sn-astra-launcher-bottom', `${bottom + height + 12}px`);
      host.style.setProperty('--sn-astra-launcher-size', `${height}px`);
      host.style.setProperty('--sn-astra-launcher-right', appearance.right);
      host.setAttribute('data-sn-astra-aligned', '');
      fitCallout();
    }
    if (whatsapp) {
      const sizes = new ResizeObserver(alignLaunchers);
      sizes.observe(whatsapp);
      const stickyBuy = document.querySelector('.sn-sticky-buy');
      if (stickyBuy) {
        sizes.observe(stickyBuy);
        new MutationObserver(alignLaunchers).observe(stickyBuy, {
          attributes: true, attributeFilter: ['hidden', 'class', 'style']
        });
      }
    }
    function fitViewport() {
      const viewport = window.visualViewport;
      const visibleHeight = viewport?.height ?? window.innerHeight;
      const visibleBottom = (viewport?.offsetTop ?? 0) + visibleHeight;
      host.style.setProperty('--sn-astra-panel-height',
        `${Math.max(0, Math.min(window.innerHeight * .7, visibleHeight - 24))}px`);
      host.style.setProperty('--sn-astra-panel-bottom',
        `${Math.max(12, window.innerHeight - visibleBottom + 12)}px`);
      alignLaunchers();
    }
    fitViewport();
    window.addEventListener('resize', fitViewport, { passive: true });
    window.visualViewport?.addEventListener('resize', fitViewport, { passive: true });
    window.visualViewport?.addEventListener('scroll', fitViewport, { passive: true });
  }
  attach();
})();
